/**
 * The one action list (docs/ux/artboards/optimal decision 6): `actions(entity, ctx)` returns the
 * ordered verbs for a run, task, decision, landing, build, release lane, bark or artifact. Row buttons, the
 * inspector footer, ⌘K, the status line, the key handler and the `viko` command echo all read
 * this list, so a label, a key or a command is defined exactly once.
 *
 * `vikoCommand` is the terminal equivalent, matching apps/daemon/src/cli/commands.ts
 * (status | needs | bark | answer | land | open | logs | tail). It is null for an action the CLI
 * does not ship yet; `NO_CLI_EQUIVALENT` lists those, and a test keeps the list honest.
 *
 * Nothing here touches the app: `ctx.do(verb, entity)` performs an action, so each screen decides
 * how (navigate, send an answer, copy). Keys are single presses (`s`, `3`, `⇧L`); `⏎` belongs to
 * the focused row and is never matched by the key handler.
 */

export type EntityKind =
  | 'run'
  | 'task'
  | 'decision'
  | 'landing'
  | 'build'
  | 'bark'
  | 'artifact'
  | 'lane'

export interface DecisionOption {
  /** 1-based, the digit key and `viko answer <id> <n>`. */
  n: number
  label: string
  suggested?: boolean
}

export type ActionEntity =
  | { kind: 'run'; id: string; live?: boolean; branch?: string; title?: string }
  | { kind: 'task'; id: string; runId?: string; title?: string }
  | {
      kind: 'decision'
      id: string
      runId?: string
      title?: string
      options?: readonly DecisionOption[]
    }
  | {
      kind: 'landing'
      id: string
      branch?: string
      title?: string
      landed?: boolean
      /** What `land.merge` needs; without them Land is shown disabled. */
      machineId?: string
      workstreamId?: string
      projectId?: string
      runId?: string
    }
  | { kind: 'build'; id: string; failed?: boolean; title?: string }
  | { kind: 'bark'; id: string; title?: string }
  | { kind: 'artifact'; id: string; title?: string }
  | {
      /** A release lane (viko.project.yaml `releaseLanes`): `land.deploy` builds or releases it. */
      kind: 'lane'
      id: string
      projectId: string
      /** The machine that builds it; without one Build is shown disabled. */
      machineId?: string
      production?: boolean
      /** Button label from the strip ("Build 3 changes", "Retry build"). */
      label?: string
      title?: string
    }

export interface Action {
  /** Unique within the entity: `land`, `pick2`, `stop`. */
  id: string
  label: string
  /** As shown on a keycap: `s`, `3`, `⇧L`, `⏎`. */
  key: string
  /** The `viko …` terminal equivalent, or null until the CLI ships one. */
  vikoCommand: string | null
  run: () => void
  danger?: boolean
  disabledReason?: string
  /** One of a decision's numbered answers (the answer bar owns these; footers fold them). */
  option?: boolean
  /** Already a labelled button in the decision's answer bar: the footer does not repeat it. */
  inBar?: boolean
}

export interface ActionCtx {
  /** Performs `verb` (an action id) on `entity`. */
  do: (verb: string, entity: ActionEntity) => void
  /** Why a verb cannot run right now (a policy, a missing machine…); absent = available. */
  unavailable?: (verb: string, entity: ActionEntity) => string | undefined
}

interface Spec {
  id: string
  label: string
  key: string
  cmd: string | null
  danger?: boolean
  option?: boolean
  inBar?: boolean
  /** Reason the verb is unavailable for this entity by its own state. */
  off?: string | undefined
}

const q = (s: string) => (/^[\w./:@-]+$/.test(s) ? s : JSON.stringify(s))

function specs(e: ActionEntity): Spec[] {
  switch (e.kind) {
    case 'run':
      return [
        { id: 'open', label: 'Open conversation', key: '⏎', cmd: `viko open run:${e.id}` },
        { id: 'tail', label: 'Follow live', key: 't', cmd: `viko tail run ${e.id}` },
        { id: 'logs', label: 'Recent logs', key: '⇧T', cmd: `viko logs run ${e.id}` },
        { id: 'land', label: 'Land', key: '⇧L', cmd: `viko land ${q(e.branch ?? e.id)}` },
        { id: 'reply', label: 'Steer', key: 'r', cmd: null },
        {
          id: 'stop',
          label: 'Stop',
          key: '⇧S',
          cmd: null,
          danger: true,
          off: e.live ? undefined : 'This run is not running.',
        },
      ]
    case 'task':
      return [
        {
          id: 'open',
          label: 'Open run',
          key: '⏎',
          cmd: e.runId ? `viko open run:${e.runId}` : null,
          off: e.runId ? undefined : 'No run yet. It starts when a machine frees up.',
        },
        {
          id: 'tail',
          label: 'Follow live',
          key: 't',
          cmd: e.runId ? `viko tail run ${e.runId}` : null,
          off: e.runId ? undefined : 'No run yet.',
        },
        {
          id: 'reply',
          label: 'Steer',
          key: 'r',
          cmd: null,
          off: e.runId ? undefined : 'No run yet. Steer it once it starts.',
        },
      ]
    case 'decision': {
      const opts = e.options ?? []
      const suggested = opts.find((o) => o.suggested)
      return [
        ...opts.slice(0, 9).map(
          (o): Spec => ({
            id: `pick${o.n}`,
            label: o.label,
            key: String(o.n),
            cmd: `viko answer ${e.id} ${o.n}`,
            option: true,
          }),
        ),
        // app-clarity: the always-visible note (n) comes first; s and r stay as key aliases.
        { id: 'note', label: 'Note', key: 'n', cmd: null, inBar: true },
        {
          id: 'delegate',
          label: 'Decide for me',
          key: 'd',
          cmd: `viko answer ${e.id} --delegate`,
          inBar: true,
        },
        { id: 'snooze', label: 'Later', key: 'e', cmd: null, inBar: true },
        {
          id: 'suggested',
          label: 'Accept the suggested answer',
          key: 's',
          cmd: suggested ? `viko answer ${e.id} ${suggested.n}` : null,
          off: suggested ? undefined : 'No suggested answer.',
          // An alias of picking the suggested option (its key `s` stays); not a second button.
          inBar: true,
        },
        {
          id: 'reply',
          label: 'Write your own answer',
          key: 'r',
          cmd: `viko answer ${e.id} --text "…"`,
          inBar: true,
        },
        // In the answer bar's More (…) menu, ⌘K and the status line; not a footer row.
        { id: 'turns', label: 'Show turns', key: 'v', cmd: null, inBar: true },
        {
          id: 'session',
          label: 'Open in session',
          key: '⇧V',
          cmd: e.runId ? `viko open run:${e.runId}` : null,
          off: e.runId ? undefined : 'This ask is not tied to a session.',
          inBar: true,
        },
      ]
    }
    case 'landing':
      return [
        {
          id: 'land',
          label: 'Land',
          key: '⇧L',
          cmd: `viko land ${q(e.branch ?? e.id)}`,
          off: e.landed ? 'Already landed.' : undefined,
        },
        {
          id: 'details',
          label: 'Commits, files and tests',
          key: '⇧D',
          cmd: `viko open landing:${e.id}`,
        },
        { id: 'tail', label: 'Follow live', key: 't', cmd: `viko tail landing ${e.id}` },
        { id: 'logs', label: 'Recent logs', key: '⇧T', cmd: `viko logs landing ${e.id}` },
      ]
    case 'build':
      return [
        { id: 'log', label: 'Open log', key: '⇧T', cmd: null },
        { id: 'artifact', label: 'Open artifact', key: '⇧A', cmd: null },
        {
          id: 'retry',
          label: 'Retry',
          key: '⇧R',
          cmd: null,
          off: e.failed ? undefined : 'Only a failed build can be retried.',
        },
      ]
    case 'lane':
      return [
        {
          id: 'build',
          label: e.production ? 'Release with passkey' : (e.label ?? 'Build now'),
          key: '⇧B',
          cmd: null,
          off: e.machineId ? undefined : 'No enrolled machine builds this lane yet.',
        },
      ]
    case 'bark':
      return [{ id: 'open', label: 'Open', key: '⏎', cmd: `viko open bark:${e.id}` }]
    case 'artifact':
      return [
        { id: 'open', label: 'Open', key: '⏎', cmd: `viko open artifact:${e.id}` },
        { id: 'link', label: 'Copy link', key: 'y', cmd: null },
      ]
  }
}

export function actions(entity: ActionEntity, ctx: ActionCtx): Action[] {
  return specs(entity).map((s) => {
    const disabledReason = s.off ?? ctx.unavailable?.(s.id, entity)
    return {
      id: s.id,
      label: s.label,
      key: s.key,
      vikoCommand: s.cmd,
      run: () => {
        if (!disabledReason) ctx.do(s.id, entity)
      },
      ...(s.danger ? { danger: true } : {}),
      ...(s.option ? { option: true } : {}),
      ...(s.inBar ? { inBar: true } : {}),
      ...(disabledReason ? { disabledReason } : {}),
    }
  })
}

/**
 * Actions with no CLI command yet, as `kind.verb` (digits folded to N). Open question 4 in the
 * optimal README lists what ships next: stop, resume, snooze, fix.
 */
export const NO_CLI_EQUIVALENT: readonly string[] = [
  'artifact.link',
  'build.artifact',
  'build.log',
  'build.retry',
  'lane.build',
  'decision.note',
  'decision.snooze',
  'decision.turns',
  'run.reply',
  'run.stop',
  'task.open',
  'task.reply',
  'task.tail',
]

/** `s`, `3`, `⇧L`, `⏎` as a matchable key. */
export function parseKey(key: string): { key: string; shift: boolean } | null {
  if (key === '⏎') return { key: 'Enter', shift: false }
  if (key.startsWith('⇧') && key.length === 2) return { key: key[1] as string, shift: true }
  return key.length === 1 ? { key, shift: false } : null
}

/** True when `e` is this action's key with no ⌘/Ctrl/Alt. Enter is never matched here. */
export function keyMatches(key: string, e: KeyboardEvent): boolean {
  const k = parseKey(key)
  if (!k || k.key === 'Enter') return false
  if (e.metaKey || e.ctrlKey || e.altKey) return false
  return e.key === k.key
}

/** Every verb the keyboard can run, first match wins. */
export function actionForKey(list: readonly Action[], e: KeyboardEvent): Action | undefined {
  return list.find((a) => keyMatches(a.key, e))
}
